import api from '../../services/api';
import { sealFields } from '../../utils/spaceKeys';

// THE GRAPH'S MODEL (parity with nucleus.oS Graph.swift + GraphMore.swift +
// GraphEdit.swift): every node the space you are in holds, narrowed by schema,
// found by keyword, sorted, filtered — and the trash. Personal paths are
// /nodes/…; in a group they are /groups/<id>/nodes/… (the group branch is
// where our bugs live, so every loader here takes the group and branches once).

export const PAGE = 60;

export const SORTS = [
    { id: 'updated', word: 'Last changed', field: 'updated_at', order: 'desc' },
    { id: 'updated-asc', word: 'Least recently changed', field: 'updated_at', order: 'asc' },
    { id: 'created', word: 'Newest', field: 'created_at', order: 'desc' },
    { id: 'created-asc', word: 'Oldest', field: 'created_at', order: 'asc' },
    { id: 'title', word: 'Name A–Z', field: 'title', order: 'asc' },
    { id: 'title-desc', word: 'Name Z–A', field: 'title', order: 'desc' },
];
export const sortOf = (id) => SORTS.find((s) => s.id === id) || SORTS[0];
/** The sort a saved view names, by field and order (older views carry no order). */
export const sortFrom = (field, order) => (SORTS.find((s) => s.field === field && s.order === (order || (field === 'title' ? 'asc' : 'desc'))) || SORTS[0]).id;

export const STRATA = ['raw', 'inferred', 'declared'];
export const MATCHES = [
    { id: 'contains', word: 'contains' },
    { id: 'exact', word: 'is exactly' },
    { id: 'starts_with', word: 'starts with' },
];

// `links`: whether relationship nodes are among the rows. A relationship is a
// node, so without this a graph's list is its entities and its links interleaved;
// hidden by default, and not a filter (filterIsEmpty and the rail's count ignore it).
export const EMPTY_FILTER = {
    access: 'owned', schemas: [], apps: [], rels: [], strata: [], links: false,
    dateField: 'updated_at', after: '', before: '', range: 'any', field: '', value: '', match: 'contains',
};
export const filterIsEmpty = (f) => !f || (
    (f.access || 'owned') === 'owned' && !f.schemas?.length && !f.apps?.length && !f.rels?.length && !f.strata?.length
    && !f.after && !f.before && !f.field
);

/** A schema's colour, steady across launches: hashed from its name (SchemaTint). */
export function tintOf(name = '') {
    let h = 2166136261;
    for (const ch of new TextEncoder().encode(String(name))) h = Math.imul(h ^ ch, 16777619) >>> 0;
    // HSB(h, .38, .78) as HSL
    return `hsl(${h % 360} 40% 63%)`;
}

export const isSealed = (v) => typeof v === 'string' && v.startsWith('paradigm_enc:');

/** A value as text, for READING: strings verbatim, lists of strings joined, else JSON. */
export function textOf(v) {
    if (v == null) return '';
    if (typeof v === 'string') return v;
    if (Array.isArray(v) && v.every((x) => typeof x === 'string')) return v.join(', ');
    try { return JSON.stringify(v); } catch { return String(v); }
}

// ── the editor's rows: a value keeps its type through an edit ─────────────
// A string is edited as text and saved as that exact text — "123" stays the
// string "123". Anything else (a number, true/false, null, a list, an object)
// is edited as JSON and saved as what that JSON says, so a list stays a list.
let rowSeq = 0;
export function rowOf(key, v, { known = true } = {}) {
    const sealed = isSealed(v);
    if (v === undefined || typeof v === 'string') return { id: ++rowSeq, key, kind: 'text', text: v ?? '', sealed, known };
    const text = v !== null && typeof v === 'object' && !Array.isArray(v) ? JSON.stringify(v, null, 2) : JSON.stringify(v);
    return { id: ++rowSeq, key, kind: 'json', text, sealed: false, known };
}

/** A blank row for a schema property, typed by what the schema declares. */
export function rowForProperty(key, prop) {
    const t = Array.isArray(prop?.type) ? prop.type.find((x) => x !== 'null') : prop?.type;
    return { ...rowOf(key, undefined, { known: false }), kind: !t || t === 'string' ? 'text' : 'json' };
}

/** The value a row holds. Throws when a JSON row doesn't parse. `undefined` = leave the key out. */
export function valueOfRow(r, original = {}) {
    const k = r.key.trim();
    if (r.sealed) return original[k];
    if (r.kind === 'text') {
        if (r.text === '' && !r.known) return undefined;           // a blank row you added, never filled
        return r.text;
    }
    if (!r.text.trim()) {
        if (!r.known) return undefined;
        throw new Error(`“${k}” is empty. Enter JSON, or remove the field with −.`);
    }
    try { return JSON.parse(r.text); } catch { throw new Error(`“${k}” isn’t valid JSON. Switch it to text (Aa) to keep it as words.`); }
}

/** A node's readable fields, sorted, skipping the empty ones. */
export function fieldsOf(valueJson) {
    return Object.keys(valueJson || {}).sort().map((k) => [k, textOf(valueJson[k])]).filter(([, v]) => v);
}

/** The first thing worth reading in it, for the cell's face. */
export function previewOf(node) {
    const f = fieldsOf(node?.value_json).filter(([, v]) => !isSealed(v));
    return (f.find(([, v]) => v.length > 12) || f[0] || [])[1] || null;
}

export const schemaOf = (node) => node?.schema_name || 'freeform';
export const titleOf = (node) => (node?.title && String(node.title).trim()) || 'Untitled';
export const isFile = (node) => !!(node?.value_json?.blob_key || node?.value_json?.mime_type || node?.schema_name === 'rawfile');
/** Ratified = declared: the backend's ratify writes stratum 'declared' (proposal_utils, nodes_pkg/single.py). */
export const isRatified = (node) => node?.stratum === 'declared';
/** A node another person owns, reached because they shared it (personal space, Shared / All). */
export const isTheirs = (node, group, userId) => !group && !!userId && !!node?.owner_id && String(node.owner_id) !== String(userId);
export const ownerOf = (node) => node?.owner?.username || node?.owner?.display_name || node?.owner_username || null;

/** Days until `iso` (the trash's purge date), or null. */
export function daysUntil(iso) {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    return Math.max(0, Math.ceil((t - Date.now()) / 86400000));
}

/** The one schema a field filter can be scoped to: the column's, or the filter's only kind. */
export function fieldSchemaOf(scope, filter) {
    if (scope?.kind === 'schema') return scope.id || null;
    const s = filter?.schemas || [];
    return s.length === 1 && s[0] !== '__none__' ? s[0] : null;
}

function filterParams(filter, { group, scope } = {}) {
    const q = {};
    const f = { ...EMPTY_FILTER, ...(filter || {}) };
    if (f.apps.length) q.app_names = f.apps.join(',');
    if (f.rels.length) q.relationship_types = f.rels.join(',');
    // the group list has no stratum filter; sending it would read as applied
    if (!group && f.strata.length) q.stratum = f.strata.join(',');
    if (scope?.kind === 'schema') q.schema_ids = scope.id || '__none__';
    else if (f.schemas.length) q.schema_ids = f.schemas.join(',');
    // the links stay out unless asked for — or a kind was picked, which is what it is
    else if (!f.links) q.include_relationships = 'false';
    if (f.after || f.before) q.date_field = f.dateField || 'updated_at';
    if (f.after) q.date_after = new Date(f.after.length > 10 ? f.after : `${f.after}T00:00:00`).toISOString();
    if (f.before) q.date_before = new Date(`${f.before}T23:59:59`).toISOString();
    // a field filter is only meaningful against ONE schema — the route 400s otherwise
    const sid = fieldSchemaOf(scope, f);
    if (f.field && f.value && sid) {
        q.schema_id = sid;
        q.value_json_field = f.field;
        q.value_json_value = f.value;
        if (f.match && f.match !== 'contains') q.value_json_match = f.match;
    }
    return q;
}

/** The list route's query for a filter (the filter rail's counts read the same). */
export const filterParamsOf = (filter, opts) => filterParams(filter, opts);

/** One page of the space's nodes: { nodes, total, autoPurge }. `offset` reads a
 *  later page (Show more); `total: false` skips the count, which a later page
 *  already knows and which costs the server a second pass over the whole set. */
export async function loadNodes({ group, scope, search, sort, filter, limit = PAGE, offset = 0, total: counted = true }) {
    const s = sortOf(sort);
    if (scope?.kind === 'trash') {
        const r = await api.getTrashedNodes(group ? { limit: 200, group_id: group } : { limit: 200 });
        const nodes = r?.nodes || [];
        const q = (search || '').trim().toLowerCase();
        const hit = q ? nodes.filter((n) => titleOf(n).toLowerCase().includes(q)) : nodes;
        return { nodes: hit, total: hit.length, autoPurge: !!r?.auto_purge_enabled };
    }
    const q = { ...filterParams(filter, { group, scope }), sort: s.field, order: s.order };
    if (search) q.search = search;
    const page = { limit: String(limit), offset: String(offset), skip_total: counted ? 'false' : 'true' };
    if (group) {
        const r = await api.getGroupNodes(group, { ...q, ...page });
        return { nodes: r?.nodes || [], total: r?.total ?? (r?.nodes || []).length };
    }
    const access = filter?.access || 'owned';
    const params = new URLSearchParams({
        ...q, access, ...page,
        ...(access !== 'owned' ? { include_owner: 'true' } : {}),
    });
    const r = await api.request(`/nodes?${params}`);
    return { nodes: r?.nodes || [], total: r?.total ?? (r?.nodes || []).length };
}

/** One node by id, through the space's own route (for a ?node= link off the loaded page). */
export async function loadNode(group, id) {
    const r = await api.request(group ? `/groups/${group}/nodes/${id}` : `/nodes/${id}`);
    return r?.node || r;
}

/** Each schema with its count, largest first ("Freeform" for none). */
export async function loadSchemaCounts(group) {
    const r = await api.request(group ? `/groups/${group}/nodes/count?group_by=schema_id` : '/nodes/count?group_by=schema_id');
    return (r?.counts || []).map((c) => {
        const id = c.schema_id && c.schema_id !== '__none__' ? c.schema_id : null;
        return { id, key: id || 'none', name: c.schema_name || 'Freeform', count: c.count || 0 };
    }).sort((a, b) => b.count - a.count);
}

/** A schema's properties and encrypted fields, for the editor and the field filter. */
export async function loadSchemaInfo(id) {
    const s = await api.getSchema(id);
    const d = s?.schema || s || {};
    return {
        id, name: d.schema_name, properties: d.json_schema?.properties || {}, encrypted: d.encrypted_fields || [],
        required: Array.isArray(d.json_schema?.required) ? d.json_schema.required : [],
    };
}

/** The schema's required fields a value leaves out or leaves blank, in the schema's order. */
export function missingRequired(info, value) {
    const blank = (v) => v === undefined || v === null || (typeof v === 'string' && !v.trim());
    return (info?.required || []).filter((k) => blank(value?.[k]));
}

// ── acts, branching once on the space ──────────────────────────────────────
const nodeBase = (group, id) => (group ? `/groups/${group}/nodes/${id}` : `/nodes/${id}`);
const pointer = (k) => `/value_json/${String(k).replace(/~/g, '~0').replace(/\//g, '~1')}`;
const patch = (group, id, ops) => api.request(nodeBase(group, id), {
    method: 'PATCH', headers: { 'Content-Type': 'application/json-patch+json' }, body: ops,
});

export const restore = (group, id, opts) => (group ? api.restoreGroupNode(group, id, opts) : api.restoreNode(id, opts));
export const purge = (group, id) => (group ? api.purgeGroupNode(group, id) : api.purgeNode(id));
export const upload = (group, file) => api.uploadFileTo(group ? `/groups/${group}/nodes` : '/nodes', file);
/** Ratify has no group route: a group node is owned by the group, and /nodes/<id>/ratify only reaches yours. */
export const ratify = (id, on) => (on ? api.ratifyNode(id) : api.unratifyNode(id));

/**
 * Save an edited node. PUT merges and can never clear a field (CLAUDE.md), so
 * the fields go as ONE JSON Patch that replaces /value_json whole: a field you
 * removed is gone, a field you emptied is empty, every value keeps its type.
 * A new schema (personal only — the group route has no schema change, and no
 * route can return a node to freeform) goes by PUT first, then the removals.
 */
export async function saveNode(group, node, { title, schemaId, value }, encKeys, { opened = null, groupName = null } = {}) {
    let v = value;
    // the fields the server checks this write against: the node's own schema
    // version (an edit keeps it), or the new schema's when you change it
    const sid = schemaId || node.schema_id || null;
    if (sid) {
        const s = await api.getSchema(sid).catch(() => null);
        const enc = (s?.schema || s)?.encrypted_fields || [];
        // in a group, sealed to the group key; a field you didn't change keeps its ciphertext
        if (enc.length) v = await sealFields(v, enc, { group, groupName, encKeys, node, opened });
    }
    if (!group && schemaId && schemaId !== node.schema_id) {
        await api.updateNode(node.id, { title, schema_id: schemaId, value_json: v });
        const gone = Object.keys(node.value_json || {}).filter((k) => !(k in v));
        if (gone.length) await patch(null, node.id, gone.map((k) => ({ op: 'remove', path: pointer(k) })));
        return;
    }
    await patch(group, node.id, [
        { op: 'replace', path: '/title', value: title },
        { op: 'replace', path: '/value_json', value: v },
    ]);
}

/** Relate two nodes in the space: a relationship is a node, written where the space writes. */
// `belongs_to` is not a word you may give a link: it is the real belongs_to, its
// own schema, which gives the node the other node's audience. Typed as a link
// type it made a look-alike that did nothing; so it is routed to the real one.
export const BELONGS_TO = 'belongs_to';
export const isBelongsTo = (type) => String(type || '').trim().toLowerCase().replace(/[\s-]+/g, '_') === BELONGS_TO;
export async function relate(group, fromId, toId, type) {
    const real = isBelongsTo(type);
    const value_json = { from: [String(fromId)], to: [String(toId)] };
    // the schema IS belongs_to's type: a relationship_type on it would let it pose as an ordinary link
    if (!real) value_json.relationship_type = type;
    return api.request(group ? `/groups/${group}/nodes` : '/nodes', {
        method: 'POST', body: { title: real ? BELONGS_TO : type, schema_name: real ? BELONGS_TO : 'relationship', value_json },
    });
}
export const unrelate = (group, relId) => (group ? api.deleteGroupNode(group, relId) : api.deleteRelationship(relId));

/** Nodes in the space matching a keyword (the relate picker). */
export async function findNodes(group, q) {
    const p = { limit: '30', ...(q ? { search: q } : {}) };
    if (group) {
        const r = await api.getGroupNodes(group, { page: '1', per_page: '30', ...(q ? { search: q } : {}) });
        return r?.nodes || [];
    }
    const r = await api.request(`/nodes?${new URLSearchParams(p)}`);
    return r?.nodes || [];
}

/** A node's versions, newest first, through the space's history route. */
export async function loadVersions(group, id) {
    const r = await api.request(`${nodeBase(group, id)}/history?include_snapshots=true&limit=50`);
    return (r?.history || []).filter((h) => h.version != null).sort((a, b) => b.version - a.version);
}
export const revert = (group, id, version) => api.request(`${nodeBase(group, id)}/revert/${version}`, { method: 'POST' });
